import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './FlipBook.css';

interface FlipBookProps {
  text: string;
  heading?: string;
  pageKey?: string; // e.g., the word being defined, used to determine flip direction
}

/**
 * FlipBook renders a book-like container that flips to a new page
 * whenever the `text` prop changes.
 */
const FlipBook: React.FC<FlipBookProps> = ({ text, heading, pageKey }) => {
  const [frontText, setFrontText] = useState<string>('');
  const [backText, setBackText] = useState<string>('');
  const [flipped, setFlipped] = useState<boolean>(false);
  const [flipDirection, setFlipDirection] = useState<'forward' | 'backward'>('forward');
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const isFirst = useRef(true);
  const frontPageRef = useRef<HTMLDivElement | null>(null);
  const backPageRef = useRef<HTMLDivElement | null>(null);
  const prevKeyRef = useRef<string | undefined>(undefined);
  const flippedRef = useRef<boolean>(false);

  const measureHeight = () => {
    const isFlipped = flippedRef.current;
    const visibleEl = (isFlipped ? backPageRef.current : frontPageRef.current);
    const visibleHeight = visibleEl?.offsetHeight ?? 0;
    const h = Math.max(visibleHeight, 180); // keep a sensible minimum
    setContainerHeight(h);
  };

  // Initialize with first text without animation
  useEffect(() => {
    // Determine flip direction based on pageKey (dictionary-style ordering)
    if (prevKeyRef.current !== undefined && typeof pageKey === 'string') {
      const prevKey = prevKeyRef.current.toLowerCase();
      const nextKey = pageKey.toLowerCase();
      if (nextKey > prevKey) setFlipDirection('forward'); else if (nextKey < prevKey) setFlipDirection('backward');
    }
    if (typeof pageKey === 'string') prevKeyRef.current = pageKey;

    if (isFirst.current) {
      setFrontText(text);
      isFirst.current = false;
      // Allow content to render before measuring
      requestAnimationFrame(measureHeight);
      return;
    }

    if (flipped) {
      // Currently showing back -> next goes to front
      setFrontText(text);
      setFlipped(false);
    } else {
      // Currently showing front -> next goes to back
      setBackText(text);
      setFlipped(true);
    }
  }, [text]);

  // After flip state or text swaps, recalc height
  useLayoutEffect(() => {
    measureHeight();
  }, [frontText, backText, flipped]);

  // Keep ref in sync with state and measure on state change
  useEffect(() => {
    flippedRef.current = flipped;
    measureHeight();
  }, [flipped]);

  // Recalculate on resize for responsive wrapping
  useEffect(() => {
    const onResize = () => measureHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Observe page size changes for dynamic reflow
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureHeight());
    if (frontPageRef.current) ro.observe(frontPageRef.current);
    if (backPageRef.current) ro.observe(backPageRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="flipbook-wrapper">
      {heading && <h3 className="flipbook-heading">{heading}</h3>}
      <div
        className={`book ${flipped ? (flipDirection === 'forward' ? 'flipped-forward' : 'flipped-backward') : ''}`}
        style={{ height: containerHeight || undefined }}
        aria-live="polite"
      >
        <div className="book-inner">
          {/* Front */}
          <div className="book-face book-front">
            <div className="page" ref={frontPageRef}>
              <div className="page-gutter" aria-hidden="true" />
              <ReactMarkdown className="page-content" remarkPlugins={[remarkGfm]}>
                {frontText}
              </ReactMarkdown>
              <div className="page-edge" aria-hidden="true" />
            </div>
          </div>

          {/* Back */}
          <div className="book-face book-back">
            <div className="page" ref={backPageRef}>
              <div className="page-gutter" aria-hidden="true" />
              <ReactMarkdown className="page-content" remarkPlugins={[remarkGfm]}>
                {backText}
              </ReactMarkdown>
              <div className="page-edge" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlipBook;
